import React, { useEffect, useCallback, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput, gqlAccept } from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';
import {
  isMedicycle,
  isRebattery,
  isRebatteryLiion,
  requiredString,
} from 'shared-pages/common/constants';
import { getRouteByName } from 'app/routes';

// Customize FormShape based on Customer/Company
function useCustomFormShape(base: any) {
  const { t } = useTranslation();
  const reqStr = t(requiredString);

  let formShape = Yup.object(base);

  // // Use concat to overlay stricter rules on top of the existing object schema.
  // formShape = formShape.concat(
  //   Yup.object({
  //     adrNumber: Yup.string().required(reqStr),
  //     adrExpDate: YupDate.required(reqStr),
  //   }),
  // );

  // // Update a field with shape (also immutable)
  // formShape = formShape.shape({
  //   name: Yup.string().notRequired(),
  //   vehicleId: formShape.fields['vehicleId'].required('Vehicle is required'),
  // });

  // Re Battery
  if (isRebattery || isRebatteryLiion) {
    formShape = formShape.concat(
      Yup.object({
        adrNumber: Yup.string().required(reqStr),
        adrExpDate: YupDate.required(reqStr),
        // vehicleId: YupNumeric.required(reqStr),
      }),
    );
  }

  return formShape;
}

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  paramCollectorId?: number | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const DriverFormPage = ({
  isModal = false,
  paramId = undefined,
  paramCollectorId,
  modalOnClose,
}: Props) => {
  const { handleGraphqlError } = useHandleError('driver');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    legacyAppId: YupString,
    name: YupString.required(t(requiredString)),
    notes: YupString,
    phone: YupString,
    collectorId: YupNumeric.required(t(requiredString)),
    userId: YupNumeric,
    adrNumber: YupString,
    adrExpDate: YupDate,
    adrFileId: YupNumeric,
    licenseExpDate: YupDate,
    licenseFileId: YupNumeric,
    signFileId: YupNumeric,
    isActive: YupBoolean,
    isApproved: YupBoolean,
    approvedDate: YupDate,
    approvedBy: YupString,
    vehicleId: YupNumeric,
  };

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [accept] = useMutation(gqlAccept);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (isNew) {
      // initialize new record
      initializeNewRecord();
    } else {
      // fetch data
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum]);

  /** Αρχικοποίησε Νέα Εγγραφή  */
  const initializeNewRecord = async () => {
    let newRec: any = { isActive: true };
    if (appStore.isCollector) {
      newRec = {
        ...newRec,
        collectorId: appStore.associatedId,
      };
    } else if (paramCollectorId) {
      newRec = {
        ...newRec,
        collectorId: paramCollectorId,
      };
    }
    setInitialNewRecData(newRec);
  };

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Drivers')), { ...location.state });
  }, [history, location.state]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[IDoc] Add:', mutValues);
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${rec?.id}]`);
          return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
      if (rec) {
        if (isModal) {
          if (modalOnClose) modalOnClose(rec.id);
        }
      }
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  /**
   * * APPROVE
   */
  const handleApprove = async () => {
    accept({ variables: { id: idNum } })
      .then((d) => {
        toastSuccess(`${t('Το όχημα εγκρίθηκε!')} [${d.data.acceptDriver}]`);
        refetch();
      })
      .catch((error) => {
        handleGraphqlError('mutation-accept', error);
      });
  };

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        title={isNew ? `${t('Δήλωση Νέου Οδηγού')}` : `${t('Οδηγός')}: ${data?.name}`}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onReturn={!isModal ? handleReturn : null}
        permitSubmit={false}
        permitDelete={false}
        initialValues={isNew ? initialNewRecData : pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
        injectFooterElements={
          !isModal
            ? undefined
            : (formikCtx) => {
                return (
                  <ButtonCloseModal
                    onClick={() => {
                      modalOnClose(-1);
                    }}
                  />
                );
              }
        }
      >
        <FormFields
          isNew={isNew}
          updateRecord={updateRecord}
          refetch={refetch}
          data={data}
          handleApprove={handleApprove}
        />
        {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
      </FormCard>
    );
  }
};

